import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import { scrollToId } from "@/lib/useLenis";

// Masked line-by-line reveal component
function RevealLine({ text, delay = 0, className = "" }) {
    return (
        <span className="reveal-mask block leading-[0.85]">
            <motion.span
                className={`reveal-inner block ${className}`}
                initial={{ y: "110%", rotate: 1 }}
                animate={{ y: "0%", rotate: 0 }}
                transition={{ duration: 1.05, delay, ease: [0.19, 1, 0.22, 1] }}
            >
                {text}
            </motion.span>
        </span>
    );
}

export default function Hero({ site }) {
    const { scrollY } = useScroll();
    const logoY = useTransform(scrollY, [0, 500], [0, -80]);
    const logoScale = useTransform(scrollY, [0, 500], [1, 0.9]);
    const bgY = useTransform(scrollY, [0, 800], [0, 200]);
    const bgScale = useTransform(scrollY, [0, 800], [1, 1.15]);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100svh] pt-[68px] overflow-hidden bg-ink-900"
        >
            {/* Grid lines background */}
            <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
            {/* Noise */}
            <div className="noise" />

            {/* Parallax background image */}
            <motion.div
                aria-hidden
                style={{ y: bgY, scale: bgScale }}
                className="absolute inset-0 -z-0"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-[0.28]"
                    style={{ backgroundImage: `url(${site.hero_image_url})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-ink-900 via-ink-900/30 to-ink-900" />
                <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-transparent to-ink-900/60" />
            </motion.div>

            {/* Kicker */}
            <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 pt-24 md:pt-28">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.6 }}
                    className="flex items-center gap-3 font-mono-label text-lime-signal"
                >
                    <span className="inline-block w-6 h-px bg-lime-signal" />
                    {site.hero_kicker}
                </motion.div>

                {/* Massive Headline */}
                <h1 className="mt-6 md:mt-10 font-display uppercase text-white tracking-[-0.02em] text-[15vw] md:text-[13vw] lg:text-[11vw] leading-[0.82]">
                    <RevealLine text={site.hero_line_1} delay={0.35} className="text-chrome" />
                    <RevealLine text={site.hero_line_2} delay={0.5} className="text-white" />
                    <RevealLine text={site.hero_line_3} delay={0.65} className="text-lime-signal" />
                </h1>

                {/* Sub + CTA row */}
                <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.0, duration: 0.7 }}
                        className="lg:col-span-6 max-w-xl"
                    >
                        <p className="text-white/70 text-base md:text-lg leading-relaxed">
                            {site.hero_sub}
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.7 }}
                        className="lg:col-span-6 flex flex-wrap items-center gap-4 justify-start lg:justify-end"
                    >
                        <a
                            href={`tel:${site.phone_raw || site.phone.replace(/\s+/g, "")}`}
                            data-testid="hero-call-cta"
                            className="btn-signal group text-base md:text-lg"
                        >
                            <Phone size={18} />
                            {site.cta_primary}
                            <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </a>
                        <button
                            onClick={() => scrollToId("contact")}
                            data-testid="hero-quote-cta"
                            className="btn-ghost text-base"
                        >
                            {site.cta_secondary}
                        </button>
                    </motion.div>
                </div>

                {/* Floating logo — dynamic from admin */}
                <motion.div
                    style={{ y: logoY, scale: logoScale }}
                    initial={{ opacity: 0, scale: 0.7, rotate: -12 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
                    className="pointer-events-none absolute right-[3%] top-[15%] hidden md:block"
                >
                    <div className="relative w-[280px] lg:w-[380px] xl:w-[440px]">
                        <div className="absolute inset-0 blur-3xl bg-lime-signal/25 rounded-full" />
                        <img
                            src={site.logo_url}
                            alt={site.business_name}
                            data-testid="hero-logo"
                            className="relative w-full h-auto drop-shadow-[0_20px_60px_rgba(198,255,61,0.25)]"
                        />
                    </div>
                </motion.div>

                {/* Bottom meta bar */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.3, duration: 0.8 }}
                    className="mt-16 md:mt-20 pb-10 flex flex-wrap items-center gap-6 md:gap-10 font-mono-label text-white/40"
                >
                    <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-lime-signal rounded-full animate-pulse" /> Accepting new clients</div>
                    <div>Licensed &amp; Insured</div>
                    <div>Free Estimates</div>
                    <div className="hidden md:block">Residential + Commercial</div>
                </motion.div>
            </div>

            {/* Mobile logo (below hero content) */}
            <div className="md:hidden relative flex justify-center pb-10">
                <img
                    src={site.logo_url}
                    alt={site.business_name}
                    data-testid="hero-logo-mobile"
                    className="w-48 h-auto drop-shadow-[0_10px_40px_rgba(198,255,61,0.25)]"
                />
            </div>
        </section>
    );
}
